import { cn } from './utils/cn';

/**
 * Inline indicator for a keyboard key. Use to surface keyboard shortcuts in
 * tooltips, menu items, and command palette hints.
 *
 * For multi-key shortcuts, wrap several Kbds in a {@link KbdGroup}.
 *
 * @example
 * <Kbd>⌘K</Kbd>
 */
function Kbd({ className, ...props }: React.ComponentProps<'kbd'>) {
  return (
    <kbd
      data-slot="kbd"
      className={cn(
        "pointer-events-none inline-flex h-5 w-fit min-w-5 items-center justify-center gap-1 rounded-sm bg-muted px-1 font-sans text-xs font-medium text-muted-foreground select-none in-data-[slot=tooltip-content]:bg-background/20 in-data-[slot=tooltip-content]:text-background dark:in-data-[slot=tooltip-content]:bg-background/10 [&_svg:not([class*='size-'])]:size-3",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Inline cluster of {@link Kbd} children rendered with a small gap between
 * keys. Use for shortcuts that combine modifiers (e.g. `⌘` `⇧` `P`).
 *
 * @example
 * <KbdGroup>
 *   <Kbd>⌘</Kbd>
 *   <Kbd>⇧</Kbd>
 *   <Kbd>P</Kbd>
 * </KbdGroup>
 */
function KbdGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <kbd
      data-slot="kbd-group"
      className={cn('inline-flex items-center gap-1', className)}
      {...props}
    />
  );
}

export { Kbd, KbdGroup };
